import { FormEvent, useCallback, useState } from 'react';
import { API_BASE } from '../../lib/api-base';
import {
  AppliedCoupon,
  CouponRejection,
  ValidateCouponResult,
  previewDiscount,
} from '../../lib/coupon';
import { useTranslation } from '../../lib/i18n';
import { formatMoney } from '../../lib/money';
import type { Cadence, PickerOption } from '../../lib/plans';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useLoadBi } from '../../lib/use-load-bi';
import { Button, Input, TextButton } from '../symphony/ui';
import styles from './CouponField.module.css';

/**
 * What every promo-code click reports.
 *
 * `component_name` is the FIELD, not the plan — one name for all four clicks, which is what makes
 * "how many buyers opened the promo code slot" a single row instead of one row per plan id. It used
 * to be `option.agentPlanId`: a raw GUID, unreadable in a BI table and a different value per
 * environment.
 *
 * That leaves the four clicks sharing a `component_name` in the generic (198:10) stream, which
 * carries no `button_name`. `action` is what separates them there — it is one of the registered
 * src:172 fields, so it rides along into `additionalFields` on its own.
 *
 * The plan is not lost: it moves to `plan_id` / `plan_name`, alongside the cadence the code was
 * validated against — a coupon is only ever valid for one (plan, cadence, currency) target.
 */
const couponBi = (planKey: string, option: PickerOption, cadence: Cadence) => ({
  componentName: 'have a coupon',
  additionalFields: {
    plan_id: option.agentPlanId,
    plan_name: planKey,
    cadence,
  },
});

// The promo-code slot on one paid plan card, at the card's bottom edge under the feature list.
//
// Collapsed to a text link by default — the field only matters to the small share of buyers who
// arrive with a code, and an always-open input on every card invites everyone to go hunting for one.
//
// Scoped to ONE card on purpose: a coupon is validated against a specific checkout target
// (plan + cadence + currency), so "valid" is only ever an answer about THIS card. That is also why
// an applied code is dropped whenever the target moves (see `onTargetChange` in the parent) —
// a discount resolved for Max/monthly says nothing about Max+/yearly.

export interface CouponFieldProps {
  planName: string;
  /**
   * The card key (`basic` / `pro` / `max`) — the plan's name for BI. `planName` cannot be used: it is
   * translated, so the same plan would split across 37 locales.
   */
  planKey: string;
  option: PickerOption;
  cadence: Cadence;
  currency?: string;
  applied: AppliedCoupon | null;
  onApply: (coupon: AppliedCoupon) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export function CouponField({
  planName,
  planKey,
  option,
  cadence,
  currency,
  applied,
  onApply,
  onRemove,
  disabled,
}: CouponFieldProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [rejection, setRejection] = useState<CouponRejection | 'failed' | null>(null);

  useLoadBi('coupon-validate', { isLoading: validating, isError: rejection === 'failed' });

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmed = code.trim();
      if (!trimmed || validating) return;
      logPageClickBi(
        { page_name: 'packages', button_name: 'coupon_apply', action: 'apply' },
        couponBi(planKey, option, cadence),
      );
      setValidating(true);
      setRejection(null);
      try {
        const res = await fetch(`${API_BASE}/api/premium/validate-coupon`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code: trimmed, agentPlanId: option.agentPlanId, cadence }),
        });
        if (!res.ok) {
          setRejection('failed');
          return;
        }
        const result = (await res.json()) as ValidateCouponResult;
        if (!result.valid) {
          setRejection(result.reason);
          return;
        }
        onApply({ code: result.code, discount: result.discount });
        setOpen(false);
        setCode('');
      } catch {
        setRejection('failed');
      } finally {
        setValidating(false);
      }
    },
    // logPageClickBi is a fresh ref each render; excluded so the callback identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, validating, planKey, option, cadence, onApply],
  );

  if (applied) {
    return (
      <div className={styles.root}>
        <div className={styles.applied}>
          <CheckIcon />
          <div className={styles.appliedBody}>
            <span className={styles.appliedCode}>{t('pricing.coupon.applied', { code: applied.code })}</span>
            <span className={styles.appliedDetail}>
              {describeSaving(applied, option, cadence, currency, t)}
            </span>
          </div>
          <TextButton
            size="tiny"
            onClick={() => {
              logPageClickBi(
                { page_name: 'packages', button_name: 'coupon_remove', action: 'remove' },
                couponBi(planKey, option, cadence),
              );
              onRemove();
            }}
          >
            {t('pricing.coupon.remove')}
          </TextButton>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div className={styles.root}>
        <TextButton
          size="small"
          disabled={disabled}
          onClick={() => {
            logPageClickBi(
              { page_name: 'packages', button_name: 'coupon_open', action: 'open' },
              couponBi(planKey, option, cadence),
            );
            setOpen(true);
          }}
        >
          {t('pricing.coupon.add')}
        </TextButton>
      </div>
    );
  }

  return (
    <form className={styles.root} onSubmit={submit}>
      <div className={styles.form}>
        <Input
          autoFocus
          className={styles.formInput}
          value={code}
          maxLength={64}
          disabled={disabled || validating}
          placeholder={t('pricing.coupon.placeholder')}
          aria-label={t('pricing.coupon.placeholder')}
          status={rejection ? 'error' : 'default'}
          statusMessage={rejection ? rejectionMessage(rejection, planName, cadence, t) : undefined}
          onClear={() => {
            // The field's X. It reported nothing, so a buyer who opened the slot, typed, and gave up
            // was indistinguishable in BI from one who opened it and walked away.
            logPageClickBi(
              { page_name: 'packages', button_name: 'coupon_clear', action: 'clear' },
              couponBi(planKey, option, cadence),
            );
            setCode('');
            setRejection(null);
          }}
          clearAriaLabel={t('pricing.coupon.clearAria')}
          onChange={(e) => {
            setCode(e.target.value);
            if (rejection) setRejection(null);
          }}
        />
        <Button
          type="submit"
          size="large"
          variant="outlined"
          className={styles.formApply}
          loading={validating}
          disabled={disabled || !code.trim()}
        >
          {t('pricing.coupon.apply')}
        </Button>
      </div>
    </form>
  );
}

// Every catalog discount is first-period-only (ADR-029), so the saving is described as
// "X for your first month, then Y" rather than restating the headline price — which would read as
// a permanent price cut and is exactly what the buyer would dispute on their second invoice.
function describeSaving(
  applied: AppliedCoupon,
  option: PickerOption,
  cadence: Cadence,
  currency: string | undefined,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  const preview = previewDiscount(applied.discount, option, cadence, currency);
  if (!preview) return '';
  const full = formatMoney(preview.recurringAmount, currency);
  if (preview.freeMonths != null) {
    // `full` is the recurring charge for ONE BILLING PERIOD, which on a yearly target is the whole
    // annual total — so the unit beside it has to be the CADENCE, not the coupon's free months. The
    // free-time copy used to hardcode "/month" for both cadences and rendered a yearly plan's
    // €528/year as "€528/month" (reported), a number 12× the price the card's own headline showed.
    const yearly = cadence === 'yearly';
    return preview.freeMonths === 1
      ? t(yearly ? 'pricing.coupon.freeMonthYearly' : 'pricing.coupon.freeMonth', { full })
      : t(yearly ? 'pricing.coupon.freeMonthsYearly' : 'pricing.coupon.freeMonths', {
          count: preview.freeMonths,
          full,
        });
  }
  const first = formatMoney(preview.firstPeriodAmount ?? preview.recurringAmount, currency);
  return cadence === 'yearly'
    ? t('pricing.coupon.firstYear', { price: first, full })
    : t('pricing.coupon.firstMonth', { price: first, full });
}

// A rejection must say WHY, not just "invalid": a plan- or cadence-restricted code is valid
// somewhere else on this page, and "This code is invalid" sends that buyer away.
function rejectionMessage(
  rejection: CouponRejection | 'failed',
  planName: string,
  cadence: Cadence,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  switch (rejection) {
    case 'wrong_plan':
      return t('pricing.coupon.error.wrongPlan', { planName });
    case 'wrong_cadence':
      return cadence === 'yearly'
        ? t('pricing.coupon.error.wrongCadenceYearly')
        : t('pricing.coupon.error.wrongCadenceMonthly');
    case 'expired':
      return t('pricing.coupon.error.expired');
    case 'already_used':
      return t('pricing.coupon.error.alreadyUsed');
    case 'failed':
      return t('pricing.coupon.error.failed');
    case 'currency_mismatch':
    case 'invalid':
    default:
      return t('pricing.coupon.error.invalid');
  }
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={styles.check}>
      <path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
